'use client';
import { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const scrollToForm = () => {
    const element = document.getElementById('contact-form');
    if (element) {
      const offset = 80;
      const elementPosition = element?.getBoundingClientRect()?.top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const scrollToHowItWorks = () => {
    const element = document.getElementById('hoe-werkt-het');
    if (element) {
      const offset = 80;
      const elementPosition = element?.getBoundingClientRect()?.top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
      {/* Subtle radial accent overlays — no hard background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0584F2]/05 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-[#33BEBE]/04 via-transparent to-transparent"></div>
      
      {/* Enhanced Decorative Blur Elements */}
      <div className="absolute top-10 right-0 w-96 h-96 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl animate-pulse-slow"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-accent/15 to-primary/15 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-success/10 rounded-full blur-2xl"></div>
      
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Two-Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-10 lg:gap-16 items-center">
          
          {/* LEFT COLUMN - Content */}
          <div className="relative z-10 space-y-6 lg:space-y-10">
            
            {/* Main Headline - Enhanced Typography */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-extrabold tracking-tight leading-[1.1] reveal">
              <span className="block bg-gradient-to-r from-[#0F1E2E] via-primary to-accent bg-clip-text text-transparent">
                De waterontharder
              </span>
              <span className="block mt-2 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                zonder zout,
              </span>
              <span className="block mt-2 bg-gradient-to-r from-accent via-primary to-[#0F1E2E] bg-clip-text text-transparent">
                zonder stroom,
              </span>
              <span className="block mt-2 bg-gradient-to-r from-[#0F1E2E] to-primary bg-clip-text text-transparent">
                zonder onderhoud
              </span>
            </h1>

            {/* Subheadline - Enhanced */}
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed reveal max-w-xl">
              Wetenschappelijk bewezen katalytische technologie die kalk stopt — met behoud van alle gezonde mineralen.
            </p>

            {/* 3 Checkmarks - Enhanced Design */}
            <div className="space-y-3 md:space-y-4 reveal">
              <div className="flex items-center gap-3 md:gap-4 group">
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform shrink-0">
                  <Icon name="CheckIcon" size={20} variant="solid" className="text-white" />
                </div>
                <span className="text-sm sm:text-base md:text-lg font-bold text-foreground">Geen zout of chemicaliën</span>
              </div>
              <div className="flex items-center gap-3 md:gap-4 group">
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform shrink-0">
                  <Icon name="CheckIcon" size={20} variant="solid" className="text-white" />
                </div>
                <span className="text-sm sm:text-base md:text-lg font-bold text-foreground">Geschikt voor elke woning</span>
              </div>
              <div className="flex items-center gap-3 md:gap-4 group">
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform shrink-0">
                  <Icon name="CheckIcon" size={20} variant="solid" className="text-white" />
                </div>
                <span className="text-sm sm:text-base md:text-lg font-bold text-foreground">Inclusief installatieservice</span>
              </div>
            </div>

            {/* CTA Buttons - Enhanced */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4 reveal">
              {isHydrated && (
                <>
                  <button
                    onClick={scrollToForm}
                    className="group inline-flex items-center justify-center rounded-full bg-gradient-to-r from-primary to-accent px-6 md:px-10 py-4 md:py-6 text-base md:text-lg font-bold text-white hover:shadow-2xl hover:scale-105 btn-transition shadow-cta relative overflow-hidden whitespace-nowrap min-w-[260px]"
                  >
                    <span className="relative z-10 flex items-center">
                      Vraag gratis advies aan
                      <Icon name="ArrowRightIcon" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-accent to-primary opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </button>
                  <button
                    onClick={scrollToHowItWorks}
                    className="inline-flex items-center justify-center rounded-full border-2 border-primary bg-white/90 backdrop-blur-md px-6 md:px-10 py-4 md:py-6 text-base md:text-lg font-bold text-primary hover:bg-primary hover:text-white hover:scale-105 btn-transition shadow-lg whitespace-nowrap min-w-[260px]"
                  >
                    Hoe werkt het?
                  </button>
                </>
              )}
            </div>

            {/* Trust Badges - Enhanced */}
            <div className="flex flex-wrap items-center gap-3 md:gap-6 pt-2 md:pt-4 reveal">
              <div className="flex items-center gap-2 px-3 md:px-5 py-2 md:py-3 rounded-xl bg-white/80 backdrop-blur-md shadow-lg border border-primary/10 trust-badge">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <Icon
                      key={`star-${star}`}
                      name="StarIcon"
                      size={16}
                      variant="solid"
                      className="text-warning"
                    />
                  ))}
                </div>
                <span className="text-xs md:text-sm font-bold text-foreground">4.8★</span>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Google Reviews</span>
              </div>
              <div className="flex items-center gap-2 px-3 md:px-5 py-2 md:py-3 rounded-xl bg-white/80 backdrop-blur-md shadow-lg border border-primary/10 trust-badge">
                <Icon name="ShieldCheckIcon" size={18} className="text-primary" />
                <span className="text-xs md:text-sm font-bold text-foreground">Kiwa-gecertificeerd</span>
              </div>
              <div className="flex items-center gap-2 px-3 md:px-5 py-2 md:py-3 rounded-xl bg-white/80 backdrop-blur-md shadow-lg border border-accent/10 trust-badge">
                <Icon name="CheckBadgeIcon" size={18} className="text-accent" />
                <span className="text-xs md:text-sm font-bold text-foreground">10 jaar garantie</span>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Hero Visual with 3D Guarantee Sticker */}
          <div className="relative reveal mt-4 lg:mt-0">
            <div className="relative">

              {/* Badge above the photo */}
              {isHydrated && (
                <div className="mb-4 flex justify-center">
                  <div
                    className="inline-flex items-center gap-4 rounded-2xl px-5 py-4"
                    style={{
                      background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFBF5 50%, #FFF3E0 100%)',
                      border: '2px solid #FF6B0060',
                      boxShadow: '0 6px 24px -4px rgba(255,107,0,0.30), 0 2px 8px -2px rgba(255,107,0,0.15)',
                    }}
                  >
                    {/* Left: Discount block */}
                    <div className="flex flex-col items-center justify-center text-center min-w-[72px]">
                      <div
                        className="rounded-full px-2 py-0.5 text-[10px] font-black text-white leading-none mb-1 animate-pulse"
                        style={{ background: '#FF6B00' }}
                      >
                        OP = OP
                      </div>
                      <p className="text-2xl font-black leading-none" style={{ color: '#FF6B00' }}>−€250</p>
                      <p className="text-[10px] font-bold uppercase tracking-wide leading-tight mt-0.5" style={{ color: '#92400E' }}>
                        Tijdelijk korting
                      </p>
                    </div>

                    {/* Divider */}
                    <div className="w-px self-stretch" style={{ background: '#FF6B0030' }}></div>

                    {/* Right: Guarantee block */}
                    <div className="flex items-center gap-3">
                      {/* Mini round sticker */}
                      <div className="relative flex-shrink-0 w-14 h-14">
                        <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(135deg, #F59E0B, #FBBF24, #F59E0B)' }}></div>
                        <div className="absolute inset-1 rounded-full" style={{ background: 'linear-gradient(135deg, #FBBF24, #F59E0B)' }}></div>
                        <div className="absolute inset-2 rounded-full bg-white flex flex-col items-center justify-center border-2 border-amber-300">
                          <Icon name="ShieldCheckIcon" size={14} className="text-amber-500" variant="solid" />
                          <p className="text-[7px] font-black text-[#0F1E2E] uppercase leading-none text-center mt-0.5">1 jaar</p>
                          <p className="text-[7px] font-black text-amber-500 uppercase leading-none text-center">garantie</p>
                        </div>
                        <div className="absolute inset-0 rounded-full border-2 border-dashed border-amber-400/50 animate-spin-slow"></div>
                      </div>
                      {/* Text */}
                      <div>
                        <p className="text-sm font-black text-[#0F1E2E] leading-tight">
                          Niet goed binnen 1 jaar?
                        </p>
                        <p className="text-sm font-black text-[#0F1E2E] leading-tight">
                          Geld terug!
                        </p>
                        <p className="text-xs leading-snug mt-0.5" style={{ color: '#92400E' }}>
                          Reageer snel — beperkt beschikbaar
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Product Image Container */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-blue-50 to-teal-50 border-2 border-primary/20 shadow-2xl">
                <div className="aspect-[4/5] relative">
                  <AppImage
                    src="/assets/images/Girl_in_shower-1773667942443.png"
                    alt="Vrouw geniet van een verfrissende douche dankzij FrenaCal waterontharder zonder kalk"
                    className="w-full h-full object-cover"
                    priority
                  />
                  {/* Gradient Overlay for Depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent"></div>
                </div>
              </div>

              {/* Floating Stats Badges - positioned to avoid overflow on tablet */}
              <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-gradient-to-br from-white via-white to-blue-50 rounded-2xl p-4 md:p-6 shadow-2xl border-2 border-primary/30 backdrop-blur-md z-10">
                <p className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">250k+</p>
                <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider mt-1">
                  Gebruikers wereldwijd
                </p>
              </div>

              <div className="absolute -bottom-4 -right-2 sm:-bottom-6 sm:-right-6 bg-gradient-to-br from-white via-white to-teal-50 rounded-2xl p-4 md:p-6 shadow-2xl border-2 border-accent/30 backdrop-blur-md z-10">
                <p className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">15-25+</p>
                <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider mt-1">
                  Jaar levensduur
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Logo Cloud - Certifications - Enhanced 3D */}
        <div className="mt-16 md:mt-20 lg:mt-32 reveal" style={{ perspective: '1000px' }}>
          {/* 3D Card Container */}
          <div
            className="relative rounded-3xl overflow-hidden border border-white/60 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,249,255,0.9) 50%, rgba(230,255,250,0.95) 100%)',
              boxShadow: '0 25px 60px -10px rgba(5,132,242,0.18), 0 10px 30px -5px rgba(51,190,190,0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
              transform: 'translateZ(0)',
            }}
          >
            {/* Animated gradient top border */}
            <div
              className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl"
              style={{
                background: 'linear-gradient(90deg, #0584F2, #33BEBE, #0584F2)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 3s linear infinite',
              }}
            />

            {/* Floating orbs for depth */}
            <div className="absolute -top-8 -left-8 w-32 h-32 rounded-full opacity-20 blur-2xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, #0584F2, transparent)' }} />
            <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full opacity-15 blur-2xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, #33BEBE, transparent)', animationDelay: '1s' }} />

            <div className="relative px-6 py-8 md:py-12">
              <p className="text-center text-sm font-bold text-muted-foreground uppercase tracking-widest mb-8 md:mb-12">
                Gecertificeerd door toonaangevende instanties
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 lg:gap-16">
                {[
                  { label: 'WRAS', delay: '0ms' },
                  { label: 'Kiwa', delay: '100ms' },
                  { label: 'GSA', delay: '200ms' },
                  { label: 'Water Quality Association', delay: '300ms' },
                ].map(({ label, delay }) => (
                  <div
                    key={label}
                    className="group relative cursor-pointer"
                    style={{ animationDelay: delay }}
                  >
                    {/* 3D card per logo */}
                    <div
                      className="relative px-5 py-3 rounded-2xl transition-all duration-300 group-hover:-translate-y-2"
                      style={{
                        background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(240,249,255,0.7))',
                        boxShadow: '0 4px 15px rgba(5,132,242,0.08), 0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
                        border: '1px solid rgba(5,132,242,0.12)',
                        transform: 'translateZ(0)',
                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}
                    >
                      {/* Shine overlay on hover */}
                      <div
                        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, transparent 60%)',
                        }}
                      />
                      {/* Bottom shadow that grows on hover */}
                      <div
                        className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-3/4 h-3 rounded-full blur-md opacity-0 group-hover:opacity-60 transition-all duration-300"
                        style={{ background: 'rgba(5,132,242,0.3)' }}
                      />
                      <span
                        className="relative text-xl md:text-2xl lg:text-3xl font-bold tracking-tight transition-all duration-300"
                        style={{
                          color: 'rgba(100,116,139,0.6)',
                          textShadow: '0 1px 2px rgba(255,255,255,0.8)',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.color = '#0584F2';
                          (e.currentTarget as HTMLElement).style.textShadow = '0 0 20px rgba(5,132,242,0.4), 0 1px 2px rgba(255,255,255,0.8)';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.color = 'rgba(100,116,139,0.6)';
                          (e.currentTarget as HTMLElement).style.textShadow = '0 1px 2px rgba(255,255,255,0.8)';
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
      </div>
    </section>
  );
}